import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Popover, Stack, TextField, Typography } from "@mui/material";
import ColorPickerPopoverContent from "./ColorPickerPopoverContent";

const HEX_PATTERN = /^#([0-9a-fA-F]{6})$/;

const normalizeHex = (value, fallback = "#000000") => {
  if (!value) {
    return fallback;
  }
  const trimmed = String(value).trim();
  if (HEX_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `#${trimmed.toLowerCase()}`;
  }
  return fallback;
};

const ColorPickerField = ({ label, value, onChange, allowClear = false, defaultColor = "#000000" }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const pickerColor = useMemo(() => normalizeHex(value, defaultColor), [value, defaultColor]);
  const open = Boolean(anchorEl);

  const applyColor = (next) => {
    onChange(normalizeHex(next, defaultColor));
  };

  const handleHexInput = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      if (allowClear) {
        onChange("");
      }
      return;
    }
    if (HEX_PATTERN.test(trimmed) || /^[0-9a-fA-F]{6}$/.test(trimmed)) {
      applyColor(trimmed.startsWith("#") ? trimmed : `#${trimmed}`);
    }
  };

  return (
    <Stack spacing={0.75} sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="button"
          type="button"
          aria-label={`${label}: apri selettore colore`}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            width: 40,
            height: 40,
            border: "1px solid #cbd5e1",
            borderRadius: 1,
            p: 0,
            cursor: "pointer",
            backgroundColor: value ? pickerColor : "transparent",
            backgroundImage: value
              ? "none"
              : "linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)",
            backgroundSize: "10px 10px",
            backgroundPosition: "0 0, 0 5px, 5px -5px, -5px 0",
            flexShrink: 0,
          }}
        />
        <TextField
          fullWidth
          size="small"
          value={value || ""}
          placeholder={defaultColor}
          onChange={(e) => handleHexInput(e.target.value)}
          inputProps={{ spellCheck: false }}
        />
        {allowClear ? (
          <Button size="small" onClick={() => onChange("")} sx={{ minWidth: 0, px: 1, borderRadius: 0 }}>
            Reset
          </Button>
        ) : null}
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        <ColorPickerPopoverContent color={pickerColor} onChange={applyColor} />
      </Popover>
    </Stack>
  );
};

ColorPickerField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  allowClear: PropTypes.bool,
  defaultColor: PropTypes.string,
};

export default ColorPickerField;
 